import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import FloatingAlerts from "./components/common/FloatingAlerts";
import TokenizeLanding from "./pages/TokenizeLanding";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp/SignUp";
import ResetPassword from "./pages/ResetPassword";

// Asset Owner
import AssetOwnerDashboard from "./components/AssetOwner/AssetOwnerDashboard/AssetOwnerDashboard";
import DashboardLayout from "./components/layout/DashboardLayout";
import ManageListings from "./components/AssetOwner/ManageListings/ManageListings";
import StakingPools from "./components/AssetOwner/StakingPools/StakingPools";
import IncomeDistributions from "./components/AssetOwner/IncomeDistributions/IncomeDistributions";
import DocumentsCertificates from "./components/AssetOwner/DocumentsCertificates";
import Settings from "./components/AssetOwner/Settings/Settings";
import ListNewAsset from "./components/AssetOwner/ListNewAsset/ListNewAsset";
import PropertyDetails from "./components/AssetOwner/PropertyDetails";

// Admin
import Dashboard from "./components/admin/Dashboard";
import AdminUsers from "./components/admin/AdminUsers/AdminUsers";
import AdminUserDetail from "./components/admin/AdminUsers/AdminUserDetail";
import AdminMainPage from "./components/admin/AdminMainPage/AdminMainPage";
import AdminAssetMangement from "./components/admin/AdminAssetManagement/AdminAssetManagement";
import AdminAssetDetail from "./components/admin/AdminAssetManagement/AdminAssetDetail";
import AdminStakingPools from "./components/admin/AdminStakingPools/AdminStakingPools";
import AdminLoans from "./components/admin/AdminLoans/AdminLoans";
import AdminVerifications from "./components/admin/AdminVerifications/AdminVerifications";
import AdminAnalytics from "./components/admin/AdminAnalytics/AdminAnalytics";
import AdminSettings from "./components/admin/AdminSettings/AdminSettings";
// import InvestorDashboard from './components/investor/InvestorDashboard';
import InvestorMainpage from "./components/investor/InvestorMainpage";
import InvestorStaking from "./components/investor/InvestorStaking";
import InvestorMortgage from "./components/investor/InvestorMortgage";
import InvestorIncome from "./components/investor/InvestorIncome";
import InvestorDocuments from "./components/investor/InvestorDocuments/InvestorDocuments";
import InvestorSettings from "./components/investor/InvestorSettings";
import InvestorDashboard from "./components/investor/InvestorDashboard";
import InvestorSecondaryMarket from "./components/investor/InvestorSecondaryMarket";
import InvestorViewAndInvest from "./components/investor/InvestorViewAndInvest";
import InvestorAssetDetail from "./components/investor/InvestorAssetDetail";
import ForgotPassword from "./pages/forgotPassword";
// import NafathVerification from "./pages/nafathVerification";
import VaraVerification from "./pages/VaraVerification";
import VaraInformation from "./pages/VaraInformation";
import VaraVerificationRequestSent from "./pages/VaraVerificationRequestSent";
import { authStorageGet } from "./utils/authStorage";

/** Email links sometimes use /api/auth/reset-password/:token; send users to the SPA route. */
function ApiAuthResetPasswordRedirect() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const to = qs ? `/reset-password/${token}?${qs}` : `/reset-password/${token}`;
  return <Navigate to={to} replace />;
}

const RequireAuth = ({ children }) => {
  const token = authStorageGet("token");
  return token ? children : <Navigate to="/signin" replace />;
};

function App() {
  return (
    <Router>
      <FloatingAlerts />
      <Routes>
        {/* Public / auth routes (no sidebar) */}
        <Route path="/" element={<TokenizeLanding />} />
        {/* <Route path="/nafath" element={<NafathVerification />} /> */}
        <Route path="/vara-verification" element={<VaraVerification />} />
        <Route path="/vara-information" element={<VaraInformation />} />
        <Route
          path="/vara-verification-request-sent"
          element={<VaraVerificationRequestSent />}
        />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/reset-password/:token"
          element={<ForgotPassword />}
        />
        <Route
          path="reset-password/:token"
          element={<ApiAuthResetPasswordRedirect />}
        />

        {/* Asset Owner dashboard */}
        <Route
          path="/asset"
          element={
            <RequireAuth>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          {/* /asset/dashboard */}
          <Route path="dashboard" element={<AssetOwnerDashboard />} />

          {/* /asset/listings */}
          <Route path="listings" element={<ManageListings />} />

          {/* /asset/staking-pools */}
          <Route path="staking-pools" element={<StakingPools />} />

          {/* /asset/income */}
          <Route path="income" element={<IncomeDistributions />} />

          {/* /asset/documents */}
          <Route path="documents" element={<DocumentsCertificates />} />

          {/* /asset/settings */}
          <Route path="settings" element={<Settings />} />

          {/* /asset/stripe/reauth — Stripe Connect re-auth landing */}
          <Route path="stripe/reauth" element={<Settings />} />

          {/* /asset/list-new-asset */}
          <Route path="list-new-asset" element={<ListNewAsset />} />

          {/* /asset/property-details */}
          <Route path="property-details" element={<PropertyDetails />} />
        </Route>

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        >
          {/* /admin */}
          <Route index element={<AdminMainPage />} />

          {/* /admin/users — detail routes must be registered before the list route */}
          <Route path="users/investor/:userId" element={<AdminUserDetail />} />
          <Route path="users/owner/:userId" element={<AdminUserDetail />} />
          <Route path="users" element={<AdminUsers />} />

          {/* /admin/assets — detail route before list */}
          <Route path="assets/:assetId" element={<AdminAssetDetail />} />
          <Route path="assets" element={<AdminAssetMangement />} />

          {/* /admin/staking-pools */}
          <Route path="staking-pools" element={<AdminStakingPools />} />

          {/* /admin/loans */}
          <Route path="loans" element={<AdminLoans />} />

          {/* /admin/verifications */}
          <Route path="verifications" element={<AdminVerifications />} />

          {/* /admin/analytics */}
          <Route path="analytics" element={<AdminAnalytics />} />

          {/* /admin/settings */}
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Investor */}
        <Route
          path="/investor"
          element={
            <RequireAuth>
              <InvestorMainpage />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<InvestorDashboard />} />
          <Route path="viewinvest/:assetId" element={<InvestorAssetDetail />} />
          <Route path="viewinvest" element={<InvestorViewAndInvest />} />
          <Route path="secondary-market" element={<InvestorSecondaryMarket />} />
          <Route path="staking" element={<InvestorStaking />} />
          <Route path="mortgage" element={<InvestorMortgage />} />
          <Route path="income" element={<InvestorIncome />} />
          <Route path="documents" element={<InvestorDocuments />} />
          <Route path="settings" element={<InvestorSettings />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
